<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="4" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>{{ $t("Show") }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label>{{ $t("entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="8">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1 w-50" :placeholder="$t('Search...')" />
              <b-button variant="primary" :to="{ name: 'client-members-add-page' }">
                <span class="text-nowrap">{{ $t("Add User") }}</span>
              </b-button>
              <div class="d-inline-block px-1" v-if="isTurkeyClient">
                <b-button variant="warning" @click="isSyncMembersModalActive = true">
                  <span class="text-nowrap">{{ $t("Sync Members") }}</span>
                </b-button>
              </div>

              <div class="d-inline-block px-1" v-if="isBrazilClient">
                <b-button variant="outline-primary" :disabled="isImporting" @click="triggerFileInput">
                  <b-spinner v-if="isImporting" small class="mr-50" />
                  <span class="text-nowrap">{{
                    isImporting ? $t("Importing...") : $t("Import Users")
                  }}</span>
                </b-button>
                <b-form-file ref="fileInput" v-model="selectedFile" accept=".xls,.xlsx" plain class="d-none"
                  @change="handleFileChange" />
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table ref="refUserListTable" class="position-relative" :items="fetchInstructers" responsive
        :fields="tableColumns" primary-key="id" :sort-by.sync="sortBy" show-empty
        :empty-text="$t('No matching records found')" :sort-desc.sync="isSortDirDesc">
        <template #head()="data">
          <span class="text">{{ $t(data.label.toUpperCase()) }}</span>
        </template>

        <template #cell(index)="data">
          <span class="text">{{ data.index + 1 }}</span>
        </template>
        <template #cell(fullname)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar size="32" :src="data.item.photo" :text="avatarText(data.item.fullname)" :variant="`light-${resolveUserRoleVariant(
                data.item.isOutSourced
              )}`" :to="{
                name: 'client-member-edit',
                params: { id: data.item.id },
              }" />
            </template>
            <b-link :to="{ name: 'client-member-edit', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap">
              {{ data.item.fullname }}
            </b-link>
            <small class="text-muted">{{ data.item.email }}</small>
          </b-media>
        </template>

        <template #cell(createdAt)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">
              {{ formatDate(data.item.createdAt) }}</span>
          </div>
        </template>

  
        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge pill :variant="`light-${resolveUserStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ resolveUserStatusVariantText(data.item.status) }}
          </b-badge>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              {{ $t("Showing") }} {{ dataMeta.from }} {{ $t("to") }}
              {{ dataMeta.to }} {{ $t("of") }}
              {{ dataMeta.of.value}}
              {{ $t("entries") }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalUsers" :per-page="perPage" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Sync Members Modal -->
    <sync-members-modal :is-modal-active.sync="isSyncMembersModalActive" @refetch-data="refetchData" />

    <!-- Import Result Modal -->
    <member-import-result-modal
      :is-modal-active.sync="isImportResultModalActive"
      :is-loading="isImporting"
      :result="importResult"
      @close="closeImportResultModal"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormFile,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { ref, onUnmounted, computed } from "@vue/composition-api";
import { avatarText } from "@core/utils/filter";
import UsersListFilters from "./MembersListFilters.vue";
import useUsersList from "./useMembersList";
import useImportUsersExcel from "./useImportUsersExcel";
import userStoreModule from "../memberStoreModule";
import SyncMembersModal from "./SyncMembersModal.vue";
import MemberImportResultModal from "./MemberImportResultModal.vue";
import moment from "moment";

export default {
  computed: {},
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.$el.click();
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };

      const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
        date
      );
      return formattedDate.replace(",", "");
    },
  },
  components: {
    UsersListFilters,
    SyncMembersModal,
    MemberImportResultModal,
    BFormFile,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "apps-members";

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME))
      store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME))
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
    });

    const isSyncMembersModalActive = ref(false);
    // Client kontrolü - sadece Türkiye client'ı için göster
    const isTurkeyClient = computed(() => {
      const clientKey = localStorage.getItem("client");
      return clientKey === "turkey-client";
    });
    const isBrazilClient = computed(() => {
      const clientKey = localStorage.getItem("client");
      return clientKey === "brazil-client";
    });

    const roleOptions = [
      { label: "Admin", value: "admin" },
      { label: "Author", value: "author" },
      { label: "Editor", value: "editor" },
      { label: "Maintainer", value: "maintainer" },
      { label: "Subscriber", value: "subscriber" },
    ];

    const planOptions = [
      { label: "Basic", value: "basic" },
      { label: "Company", value: "company" },
      { label: "Enterprise", value: "enterprise" },
      { label: "Team", value: "team" },
    ];

    const statusOptions = [
      { label: "Pending", value: "pending" },
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ];

    const {
      fetchInstructers,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,
      resolveUserStatusVariantText,
      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
    } = useUsersList();

    const {
      selectedFile,
      handleFileChange,
      isImporting,
      isImportResultModalActive,
      importResult,
      closeImportResultModal,
    } = useImportUsersExcel({
      onSuccess: refetchData,
    });

    return {
      selectedFile,
      handleFileChange,
      isImporting,
      isImportResultModalActive,
      importResult,
      closeImportResultModal,
      fetchInstructers,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,
      resolveUserStatusVariantText,

      roleOptions,
      planOptions,
      statusOptions,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,

      // Sync Members
      isSyncMembersModalActive,
      isTurkeyClient,
      isBrazilClient,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
